import { useMemo } from 'react';
import * as d3 from 'd3';
import * as styles from '../styles/components/bar-chart.css.ts';
import { useD3Chart } from '../hooks/useD3Chart';
import { generateSeriesColors, SELECTION_EFFECTS } from '../constants/colors';
import { useLazyVisualization } from '../hooks/useLazyVisualization';
import { ClearSelectionButton } from './common/ClearSelectionButton';
import { LazyVisualizationPlaceholder } from './common/LazyVisualizationPlaceholder';

/**
 * BarChart - Component that displays bar charts for categorical data
 * 
 * Features:
 * - Generate bar charts from categorical and numeric data
 * - SVG-based visualization using D3.js
 * - Horizontal and vertical orientation support
 * - Responsive design
 * - Tooltip display
 * - Data selection functionality by bar click
 * - Selection state visualization
 * - Multiple value series support with grouped or stacked display
 * 
 * Operation methods:
 * - Bar click: Select data rows for corresponding category (when onBarClick is set)
 * - Cmd/Ctrl+Bar click: Add selection for data rows, or deselect if already fully selected
 * 
 * Props:
 * - categories: Array of category labels
 * - values: Array of numeric values OR array of arrays for multiple series
 * - seriesNames: Names for each series (when values is array of arrays)
 * - width: SVG width (default: 400)
 * - height: SVG height (default: 300)
 * - orientation: 'vertical' or 'horizontal' (default: 'vertical')
 * - title: Chart title
 * - onBarClick: Callback function for bar clicks (receives category, seriesIndex, value, dataIndices, isAdditive)
 * - originalData: Original data index information (array of {category, seriesIndex?, value, index})
 * - selectedIndices: Array of selected row indices (displayed with different color)
 * - showValues: Show value labels on bars (default: false)
 * - stackMode: 'grouped' or 'stacked' for multiple series (default: 'grouped')
 */
interface BarChartProps {
  categories: string[];
  values: number[] | number[][];  // 単一系列または複数系列のデータ
  seriesNames?: string[];  // 各系列の名前
  width?: number;
  height?: number;
  orientation?: 'vertical' | 'horizontal';
  title?: string;
  onBarClick?: (category: string, seriesIndex: number, value: number, dataIndices: number[], isAdditive?: boolean) => void;
  originalData?: { category: string; seriesIndex?: number; value: number; index: number }[];
  selectedIndices?: number[];
  showValues?: boolean;
  barColor?: string | string[];  // 単一色または系列ごとの色配列
  selectedBarColor?: string | string[];  // 将来の拡張用に予約（現在は SELECTION_EFFECTS を使用）
  selectedBarStrokeColor?: string | string[];  // 将来の拡張用に予約（現在は SELECTION_EFFECTS を使用）
  stackMode?: 'grouped' | 'stacked';  // 複数系列の表示モード
}

export function BarChart({
  categories,
  values,
  seriesNames,
  width: defaultWidth = 400,
  height,
  orientation = 'vertical',
  title,
  onBarClick,
  originalData,
  selectedIndices = [],
  showValues = false,
  barColor,
  selectedBarColor: _selectedBarColor,  // 将来の拡張用に予約
  selectedBarStrokeColor: _selectedBarStrokeColor,  // 将来の拡張用に予約
  stackMode = 'grouped'
}: BarChartProps) {
  // データを正規化（単一系列の場合は配列の配列に変換）
  const normalizedValues = useMemo(() => {
    if (Array.isArray(values[0])) {
      return values as number[][];
    }
    return [values as number[]];
  }, [values]);
  
  // 系列名を設定
  const normalizedSeriesNames = useMemo(() => {
    if (seriesNames) return seriesNames;
    if (normalizedValues.length === 1) return ['Value'];
    return normalizedValues.map((_, i) => `Series ${i + 1}`);
  }, [seriesNames, normalizedValues]);
  
  // 色の配列を正規化
  const colorArray = useMemo(() => {
    if (barColor) {
      if (Array.isArray(barColor)) return barColor;
      // 単一色が指定された場合、全系列に適用
      return normalizedValues.map(() => barColor);
    }
    // デフォルトの色パレットを使用
    return generateSeriesColors(normalizedValues.length);
  }, [barColor, normalizedValues]);
  
  // データサイズの計算（遅延レンダリングの判定に使用）
  const dataSize = useMemo(() => {
    return originalData?.length || categories.length || 0;
  }, [originalData, categories.length]);

  // 遅延可視化フック（1,000行以上の場合はプレースホルダー表示）
  const {
    shouldRender,
    requestRender,
    isLoading,
    error,
    retry,
    notifyRenderComplete
  } = useLazyVisualization({
    initialRender: dataSize < 1000
  });

  // マージン計算
  // 水平バーチャートの場合、カテゴリ名の最大長に基づいて左マージンを計算
  const leftMargin = useMemo(() => {
    if (orientation === 'horizontal' && categories.length > 0) {
      // Canvas要素を使ってテキストの幅を測定
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        // D3.jsで実際に使用されるフォントサイズと同じにする
        context.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
        
        // すべてのカテゴリ名の幅を測定して最大値を取得
        const maxWidth = Math.max(...categories.map(cat => 
          context.measureText(cat).width
        ));
        
        // 最大幅 + パディング（目盛りとラベルの間隔 + 余白）
        // Y軸の目盛りとテキストの間にも余裕を持たせる
        return Math.max(100, Math.ceil(maxWidth) + 50);
      }
    }
    return orientation === 'horizontal' ? 80 : 50;
  }, [orientation, categories]);

  const margin = useMemo(() => ({
    top: title ? 55 : 45,
    right: 20,
    bottom: orientation === 'vertical' ? 60 : 40,
    left: leftMargin
  }), [title, orientation, leftMargin]);

  // データ変換をuseMemoで計算
  const validData = useMemo(() => {
    // インデックスマップを事前に作成してルックアップを高速化
    const indicesMap = new Map<string, number[]>();
    if (originalData) {
      originalData.forEach(d => {
        const key = `${d.category}-${d.seriesIndex ?? 'undefined'}`;
        if (!indicesMap.has(key)) {
          indicesMap.set(key, []);
        }
        indicesMap.get(key)!.push(d.index);
      });
    }

    return categories.map((cat, catIdx) => ({
      category: cat,
      values: normalizedValues.map((series, seriesIdx) => ({
        seriesIndex: seriesIdx,
        seriesName: normalizedSeriesNames[seriesIdx],
        value: series[catIdx] || 0,
        indices: indicesMap.get(`${cat}-${seriesIdx}`) || indicesMap.get(`${cat}-undefined`) || []
      }))
    }));
  }, [categories, normalizedValues, normalizedSeriesNames, originalData]);

  // 使用する高さを決定: propsのheight、それがなければ300
  const effectiveHeight = height ?? 300;

  // useD3Chartで初期化とサイズ更新を管理
  const { containerRef, svgRef, height: actualHeight } = useD3Chart<{
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    xScale: d3.ScaleBand<string> | d3.ScaleLinear<number, number>;
    yScale: d3.ScaleBand<string> | d3.ScaleLinear<number, number>;
    xScaleGroup?: d3.ScaleBand<string>;
    yScaleGroup?: d3.ScaleBand<string>;
    xAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    yAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    titleText: d3.Selection<SVGTextElement, unknown, null, undefined>;
    tooltip: d3.Selection<HTMLDivElement, unknown, HTMLElement, undefined>;
    legend?: d3.Selection<SVGGElement, unknown, null, undefined>;
  }>({
    defaultWidth,
    defaultHeight: effectiveHeight,
    monitorHeight: true,
    margin,
    setup: ({ svg, g }) => {
      // チャートグループを作成
      const chartGroup = g.append('g');

      // 軸グループを作成
      const xAxis = g.append('g').attr('class', 'x-axis');
      const yAxis = g.append('g').attr('class', 'y-axis');

      // タイトル要素を作成
      const titleText = svg.append('text')
        .attr('class', styles.title)
        .attr('text-anchor', 'middle');

      // ツールチップを作成
      const tooltip = d3.select('body').append('div')
        .attr('class', styles.tooltip)
        .style('opacity', 0);

      // スケールを初期化（型はunion型なので、後で具体的な値を設定）
      const xScale = d3.scaleBand<string>();
      const yScale = d3.scaleLinear();

      return {
        chartGroup,
        xScale,
        yScale,
        xAxis,
        yAxis,
        titleText,
        tooltip
      };
    },
    render: ({ svg, refs, width, innerWidth, innerHeight }) => {
      if (!shouldRender) return;

      let { chartGroup, xScale, yScale, xAxis, yAxis, titleText, tooltip } = refs;

      // タイトルの位置とテキストを更新
      if (title) {
        titleText
          .attr('x', width / 2)
          .attr('y', margin.top / 2)
          .text(title)
          .style('display', 'block');
      } else {
        titleText.style('display', 'none');
      }

      // validDataがない場合は早期リターン
      if (validData.length === 0) {
        notifyRenderComplete();
        return;
      }

      // スケールを計算
      // orientationに応じて適切なスケールを再作成
      let xScaleGroup: d3.ScaleBand<string> | undefined;
      let yScaleGroup: d3.ScaleBand<string> | undefined;

      if (orientation === 'vertical') {
        // 垂直モード：X軸はカテゴリ（ScaleBand）、Y軸は数値（ScaleLinear）
        const xScaleBand = d3.scaleBand<string>()
          .domain(validData.map(d => d.category))
          .range([0, innerWidth])
          .padding(0.1);
        xScale = refs.xScale = xScaleBand;

        // グループ化モードの場合、各カテゴリ内でさらに系列ごとのスケールを作成
        if (stackMode === 'grouped' && normalizedValues.length > 1) {
          xScaleGroup = d3.scaleBand()
            .domain(normalizedValues.map((_, i) => String(i)))
            .range([0, xScaleBand.bandwidth()])
            .padding(0.05);
          refs.xScaleGroup = xScaleGroup;
        }

        // Y軸のドメインを計算（積み上げの場合は合計値、グループの場合は最大値）
        const yMax = stackMode === 'stacked'
          ? d3.max(validData, d => d3.sum(d.values, v => v.value)) || 0
          : d3.max(validData, d => d3.max(d.values, v => v.value)) || 0;

        const yScaleLinear = d3.scaleLinear()
          .domain([0, yMax])
          .nice()
          .range([innerHeight, 0]);
        yScale = refs.yScale = yScaleLinear;
      } else {
        // 水平モード：X軸は数値（ScaleLinear）、Y軸はカテゴリ（ScaleBand）
        const xMax = stackMode === 'stacked'
          ? d3.max(validData, d => d3.sum(d.values, v => v.value)) || 0
          : d3.max(validData, d => d3.max(d.values, v => v.value)) || 0;

        const xScaleLinear = d3.scaleLinear()
          .domain([0, xMax])
          .nice()
          .range([0, innerWidth]);
        xScale = refs.xScale = xScaleLinear;

        const yScaleBand = d3.scaleBand<string>()
          .domain(validData.map(d => d.category))
          .range([0, innerHeight])
          .padding(0.1);
        yScale = refs.yScale = yScaleBand;

        // 水平モードでグループ化の場合、Y軸方向に系列ごとのスケールを作成
        if (stackMode === 'grouped' && normalizedValues.length > 1) {
          yScaleGroup = d3.scaleBand()
            .domain(normalizedValues.map((_, i) => String(i)))
            .range([0, yScaleBand.bandwidth()])
            .padding(0.05);
          refs.yScaleGroup = yScaleGroup;
        }
      }

      // 軸の更新
      if (orientation === 'vertical') {
        xAxis
          .attr('transform', `translate(0,${innerHeight})`)
          .call(d3.axisBottom(xScale as d3.ScaleBand<string>))
          .selectAll('text')
          .attr('transform', 'rotate(-45)')
          .style('text-anchor', 'end');

        yAxis.call(d3.axisLeft(yScale as d3.ScaleLinear<number, number>));
      } else {
        xAxis
          .attr('transform', `translate(0,${innerHeight})`)
          .call(d3.axisBottom(xScale as d3.ScaleLinear<number, number>));

        yAxis.call(d3.axisLeft(yScale as d3.ScaleBand<string>));
      }

      // バーの描画（複数系列対応）
      // データを展開（各カテゴリ×各系列）
      const flatData = validData.flatMap(d =>
        d.values.map(v => ({
          category: d.category,
          ...v
        }))
      );

      const bars = chartGroup.selectAll<SVGRectElement, typeof flatData[0]>(`.${styles.bar}`)
        .data(flatData, d => `${d.category}-${d.seriesIndex}`);

      bars.exit().remove();

      const barsEnter = bars.enter().append('rect')
        .attr('class', styles.bar)
        .style('cursor', onBarClick ? 'pointer' : 'default');

      const allBars = bars.merge(barsEnter);

      if (orientation === 'vertical') {
        if (stackMode === 'grouped' && normalizedValues.length > 1) {
          // グループ化モード
          allBars
            .attr('x', d => {
              const catX = (xScale as d3.ScaleBand<string>)(d.category) || 0;
              const groupX = xScaleGroup!(String(d.seriesIndex)) || 0;
              return catX + groupX;
            })
            .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value))
            .attr('width', xScaleGroup!.bandwidth())
            .attr('height', d => innerHeight - (yScale as d3.ScaleLinear<number, number>)(d.value));
        } else if (stackMode === 'stacked') {
          // 積み上げモード
          // 各カテゴリの累積値を計算
          const stackedData = validData.map(cat => {
            let cumulative = 0;
            return {
              category: cat.category,
              stacks: cat.values.map(v => {
                const stack = {
                  ...v,
                  y0: cumulative,
                  y1: cumulative + v.value
                };
                cumulative += v.value;
                return stack;
              })
            };
          });

          type StackedDataItem = typeof stackedData[0]['stacks'][0] & { category: string };
          const stackFlatData: StackedDataItem[] = stackedData.flatMap(d =>
            d.stacks.map(s => ({
              category: d.category,
              ...s
            }))
          );

          allBars
            .data(stackFlatData, d => `${d.category}-${d.seriesIndex}`)
            .attr('x', d => (xScale as d3.ScaleBand<string>)(d.category) || 0)
            .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.y1))
            .attr('width', (xScale as d3.ScaleBand<string>).bandwidth())
            .attr('height', d => (yScale as d3.ScaleLinear<number, number>)(d.y0) - (yScale as d3.ScaleLinear<number, number>)(d.y1));
        } else {
          // 単一系列
          allBars
            .attr('x', d => (xScale as d3.ScaleBand<string>)(d.category) || 0)
            .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value))
            .attr('width', (xScale as d3.ScaleBand<string>).bandwidth())
            .attr('height', d => innerHeight - (yScale as d3.ScaleLinear<number, number>)(d.value));
        }
      } else {
        // 水平モードの処理
        if (stackMode === 'grouped' && normalizedValues.length > 1) {
          // グループ化モード（水平）
          allBars
            .attr('x', 0)
            .attr('y', d => {
              const catY = (yScale as d3.ScaleBand<string>)(d.category) || 0;
              const groupY = yScaleGroup!(String(d.seriesIndex)) || 0;
              return catY + groupY;
            })
            .attr('width', d => (xScale as d3.ScaleLinear<number, number>)(d.value))
            .attr('height', yScaleGroup!.bandwidth());
        } else if (stackMode === 'stacked') {
          // 積み上げモード（水平）
          // 各カテゴリの累積値を計算
          const stackedData = validData.map(cat => {
            let cumulative = 0;
            return {
              category: cat.category,
              stacks: cat.values.map(v => {
                const stack = {
                  ...v,
                  x0: cumulative,
                  x1: cumulative + v.value
                };
                cumulative += v.value;
                return stack;
              })
            };
          });

          type StackedDataItem = typeof stackedData[0]['stacks'][0] & { category: string };
          const stackFlatData: StackedDataItem[] = stackedData.flatMap(d =>
            d.stacks.map(s => ({
              category: d.category,
              ...s
            }))
          );

          allBars
            .data(stackFlatData, d => `${d.category}-${d.seriesIndex}`)
            .attr('x', d => (xScale as d3.ScaleLinear<number, number>)(d.x0))
            .attr('y', d => (yScale as d3.ScaleBand<string>)(d.category) || 0)
            .attr('width', d => (xScale as d3.ScaleLinear<number, number>)(d.x1) - (xScale as d3.ScaleLinear<number, number>)(d.x0))
            .attr('height', (yScale as d3.ScaleBand<string>).bandwidth());
        } else {
          // 単一系列（水平）
          allBars
            .attr('x', 0)
            .attr('y', d => (yScale as d3.ScaleBand<string>)(d.category) || 0)
            .attr('width', d => (xScale as d3.ScaleLinear<number, number>)(d.value))
            .attr('height', (yScale as d3.ScaleBand<string>).bandwidth());
        }
      }

      // 選択状態の設定
      const hasSelection = selectedIndices.length > 0;

      allBars
        .style('fill', d => {
          const color = colorArray[d.seriesIndex] || colorArray[0];
          // 色は常に元の色を維持（単一系列でも複数系列でも）
          return color;
        })
        .style('stroke', 'none') // ストロークは使わない（縁取りなし）
        .style('stroke-width', 0)
        .style('opacity', d => {
          if (!hasSelection) return SELECTION_EFFECTS.normalOpacity;
          const isSelected = d.indices.length > 0 && d.indices.every(idx => selectedIndices.includes(idx));

          // 選択されたものは完全不透明、選択されていないものは薄く表示
          return isSelected ? SELECTION_EFFECTS.selectedOpacity : SELECTION_EFFECTS.unselectedOpacity;
        });

      // イベントハンドラー
      allBars
        .on('mouseover', function(this: SVGRectElement, event, d) {
          d3.select(this).style('opacity', 1);
          tooltip.transition()
            .duration(200)
            .style('opacity', .9);
          const seriesLabel = normalizedValues.length > 1 ? `<br/>${d.seriesName}` : '';
          tooltip.html(`${d.category}${seriesLabel}<br/>Value: ${d.value.toFixed(2)}`)
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', function(this: SVGRectElement, _event, d) {
          const isSelected = d.indices.length > 0 && d.indices.every(idx => selectedIndices.includes(idx));
          // 選択状態に応じた不透明度に戻す
          const baseOpacity = hasSelection
            ? (isSelected ? SELECTION_EFFECTS.selectedOpacity : SELECTION_EFFECTS.unselectedOpacity)
            : SELECTION_EFFECTS.normalOpacity;
          d3.select(this).style('opacity', baseOpacity);
          tooltip.transition()
            .duration(500)
            .style('opacity', 0);
        })
        .on('click', function(event, d) {
          if (onBarClick && d.indices.length > 0) {
            const isAdditive = event.metaKey || event.ctrlKey;
            onBarClick(d.category, d.seriesIndex, d.value, d.indices, isAdditive);
          }
        });

      // 値ラベルの表示
      if (showValues) {
        const labels = chartGroup.selectAll<SVGTextElement, typeof flatData[0]>(`.${styles.valueLabel}`)
          .data(flatData, d => `${d.category}-${d.seriesIndex}`);

        labels.exit().remove();

        const labelsEnter = labels.enter().append('text')
          .attr('class', styles.valueLabel);

        const allLabels = labels.merge(labelsEnter);

        if (orientation === 'vertical') {
          if (stackMode === 'grouped' && normalizedValues.length > 1) {
            allLabels
              .attr('x', d => {
                const catX = (xScale as d3.ScaleBand<string>)(d.category) || 0;
                const groupX = xScaleGroup!(String(d.seriesIndex)) || 0;
                return catX + groupX + (xScaleGroup!.bandwidth() / 2);
              })
              .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value) - 5)
              .attr('text-anchor', 'middle')
              .text(d => d.value.toFixed(1));
          } else {
            allLabels
              .attr('x', d => ((xScale as d3.ScaleBand<string>)(d.category) || 0) + (xScale as d3.ScaleBand<string>).bandwidth() / 2)
              .attr('y', d => (yScale as d3.ScaleLinear<number, number>)(d.value) - 5)
              .attr('text-anchor', 'middle')
              .text(d => d.value.toFixed(1));
          }
        } else {
          if (stackMode === 'grouped' && normalizedValues.length > 1) {
            allLabels
              .attr('x', d => (xScale as d3.ScaleLinear<number, number>)(d.value) + 5)
              .attr('y', d => {
                const catY = (yScale as d3.ScaleBand<string>)(d.category) || 0;
                const groupY = yScaleGroup!(String(d.seriesIndex)) || 0;
                return catY + groupY + (yScaleGroup!.bandwidth() / 2);
              })
              .attr('dy', '.35em')
              .text(d => d.value.toFixed(1));
          } else {
            allLabels
              .attr('x', d => (xScale as d3.ScaleLinear<number, number>)(d.value) + 5)
              .attr('y', d => ((yScale as d3.ScaleBand<string>)(d.category) || 0) + (yScale as d3.ScaleBand<string>).bandwidth() / 2)
              .attr('dy', '.35em')
              .text(d => d.value.toFixed(1));
          }
        }
      } else {
        // showValuesがfalseの場合、ラベルを削除
        chartGroup.selectAll(`.${styles.valueLabel}`).remove();
      }

      // 凡例を追加（複数系列の場合）
      if (normalizedValues.length > 1) {
        // 既存の凡例を削除
        svg.selectAll('.legend').remove();

        const legend = svg.append('g')
          .attr('class', 'legend')
          .attr('transform', `translate(${width - 100}, 20)`);

        normalizedSeriesNames.forEach((name, i) => {
          const legendRow = legend.append('g')
            .attr('transform', `translate(0, ${i * 20})`);

          legendRow.append('rect')
            .attr('width', 10)
            .attr('height', 10)
            .attr('fill', colorArray[i]);

          legendRow.append('text')
            .attr('x', 15)
            .attr('y', 9)
            .attr('font-size', '12px')
            .text(name);
        });

        refs.legend = legend;
      } else {
        // 単一系列の場合、凡例を削除
        svg.selectAll('.legend').remove();
      }

      // レンダリング完了を通知
      notifyRenderComplete();
    },
    dependencies: [shouldRender, title, categories, normalizedValues, selectedIndices, orientation, stackMode, validData, showValues, onBarClick, colorArray, normalizedSeriesNames]
  });

  const showChart = shouldRender && !error;

  return (
    <div ref={containerRef} className={styles.container} style={{ position: 'relative', height: height ?? '100%' }}>
      {/* 遅延レンダリング：プレースホルダー表示 */}
      {(!shouldRender || error) && (
        <LazyVisualizationPlaceholder
          dataSize={dataSize}
          onView={requestRender}
          visualizationType="bar chart"
          height={actualHeight}
          isLoading={isLoading}
          error={error}
          onRetry={retry}
        />
      )}

      {/* SVGチャート：常にDOMに存在させ、display制御で表示/非表示 */}
      <div style={{ display: showChart ? 'block' : 'none' }}>
        <svg ref={svgRef} className={styles.barChart} />
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          padding: '2px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <ClearSelectionButton
            onClear={() => onBarClick?.('', 0, 0, [], false)}
            hasSelection={selectedIndices.length > 0}
          />
        </div>
      </div>
    </div>
  );
}